import { Form, Formik } from 'formik';
import { type TRPCErrorShape } from '@trpc/server/rpc';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { type CreateTeamInput } from '@/types/trpc/team';
import { type Nullable } from '~/common/types';

import useAddTeamFormConfig, { type AddEditTeamFormikValues } from './useAddEditTeamFormConfig';
import { sentencize } from '../../../shared/utils/helpers';
import { trpc } from '@/config/trpc';
import type useToggle from '@/shared/hooks/UseToggle';
import useNotification from '@/shared/hooks/UseNotification';

import EditTeamFormBody from './AddEditTeamFormBody';
import KpcModal from '@/shared/components/2.0/layout/KpcModal';
import ModalFooter from '@/shared/components/2.0/forms/ModalFooter';

type Props = {
  toggle: ReturnType<typeof useToggle>;
};

const AddTeamModal: React.FC<Props> = ({ toggle }) => {
  const popperContainer = useRef<Nullable<HTMLDivElement>>(null);

  const { t } = useTranslation();
  const { sendNotification, sendSuccessMessage } = useNotification();
  const { initialValues, validationSchema } = useAddTeamFormConfig();
  const { mutateAsync: createTeamAsync } = trpc.team.create.useMutation();

  const type = t('team');

  const handleAddTeam = async (formData: AddEditTeamFormikValues) => {
    try {
      await createTeamAsync(formData as CreateTeamInput);

      sendSuccessMessage(t('typeSuccessfullyAdded', { type }));
      toggle.hide();
    } catch (error) {
      const { message } = error as TRPCErrorShape;

      sendNotification({ message, variant: 'error' });
    }
  };

  return (
    <KpcModal className="w-2/3 xl:w-1/2" title={sentencize(t('addType', { type }))} toggle={toggle}>
      <Formik
        className="pb-3"
        onSubmit={handleAddTeam}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        <Form>
          <EditTeamFormBody />
          <ModalFooter onCancel={toggle.hide} />
        </Form>
      </Formik>
      <div ref={popperContainer} />
    </KpcModal>
  );
};

export default AddTeamModal;
